import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { InputField, SelectField, SelectFieldMultiple } from '..';
import CurrencyField from '../../pages/Form/components/CurrencyField/CurrencyField';
import { getDefaultValue } from '../../pages/Form/components/PincodeField/helper';
import pincodeService from '../../pages/Form/components/PincodeField/PincodeService';
import PincodeField from '../../pages/Form/components/PincodeField/PincodeField';
import SSNField from '../../pages/Form/components/SSNField/SSNField';
import SSNFieldMasked from '../../pages/Form/components/SSNField/SSNFieldMasked';
import UserNameField from '../../pages/Form/components/userName/UserNameInputField';
import { QuestionRadioGroupMultiple } from '../../pages/Form/forms/BusinessLiteForms/components/Page6';
import PercentageInput from '../../pages/Form/forms/BusinessLiteForms/components/PercentageInput';
import { QuestionRadioGroup } from '../../pages/Form/forms/components/IndividualLite/DeclarationForm';
import SignPad from '../../pages/Form/forms/components/IndividualLite/SignPad';
import CheckBoxFieldMutiple from '../CheckBox/CheckBoxFieldMutiple';
import CountrySelectService from '../CSC/CountrySelect/CountrySelectService';
import AntDateSelect from '../DateSelects/AntDateSelect';
import YearPicker from '../DateSelects/YearPicker';
import EmailWithValidation from '../Email/EmailWithValidation';
import PhoneNumWithValidation from '../PhoneNum/PhoneNumWithValidation';
import PhoneWithOtpValidation from '../PhoneNum/PhoneWithOtpValidation';
import { AddressAutocompleter } from './AddressAutocompleter';
import AmountField from './AmountField';
import CustomTextArea from './CustomTextArea';
import { areFieldValuesEqual, generateFieldId, getAccountActivityFieldError, getFieldBaselineValue, getFieldResetValue, getFieldSemanticType, getNoneOfTheAboveSelectionError, getPrevValueFromDiffMap, getRequiredErrorMessage, isEmploymentStatusRequiringPositiveIncome, isHidePinCodeGatedShowIf, normalizeBooleanLikeValue, readValue, resolveFieldPath, setValue, shouldShowField } from './helper';
import { DEFAULT_NUMERIC_MAX_VALUE, getNumericRangeError } from './numericValidation';
import PreviousValue from './PreviousValue';
import NumberField from './NumberField';
import TermsAcknowledgement from '../TermsAcknowledgement';
import FieldClarificationDecorator from '../FieldClarification/FieldClarificationDecorator';
import { useReviewNavigationContext } from '../ReviewWizard/useReviewNavigation';
import { REVIEW_TASK_KIND } from '../../constants/reviewConstants';

const FIELD_DECORATION_OUTER_CLASS_UI_TYPES = new Set([
  'PhoneNumWithValidation',
  'amountField',
  'countrySelect',
  'countrySelectLabel',
  'currencyField',
  'date',
  'emailWithValidation',
  'input',
  'percentageInput',
  'phoneWithOTPValidation',
  'pinCode',
  'select',
  'selectMultiple',
  'ssn',
  'ssnMasked',
  'textarea',
  'yearField',
  'yearPicker',
]);

const loggedInvalidJsonFieldWarnings = new Set();

const LEGACY_MAX_TODAY_DATE_FIELDS = new Set([
  'Business_Formation_Date',
  'operationStartDate',
]);

const LEGACY_MIN_TODAY_DATE_FIELDS = new Set([
  'operateEstDate',
]);

const normalizeReviewTaskFieldPath = (path = '') => String(path || '')
  .replace(/\[(\d+)\]/g, '[]')
  .replace(/\.([0-9]+)(?=\.|$)/g, '[]');

const isArrayReviewTaskFieldPath = (path = '') => normalizeReviewTaskFieldPath(path).includes('[]');

const getArrayReviewTaskBasePath = (normalizedFieldPath = '') => {
  const normalized = normalizeReviewTaskFieldPath(normalizedFieldPath);
  if (!normalized.includes('[]')) {
    return '';
  }

  return normalized.split('[]')[0].replace(/\.$/, '');
};

const getArrayReviewTaskCollectionName = (normalizedFieldPath = '') => {
  const basePath = getArrayReviewTaskBasePath(normalizedFieldPath);
  if (!basePath) {
    return '';
  }

  const segments = basePath.split('.');
  return segments[segments.length - 1] || '';
};

const getArrayReviewTaskFieldSuffix = (normalizedFieldPath = '') => {
  const normalized = normalizeReviewTaskFieldPath(normalizedFieldPath);
  if (!normalized.includes('[]')) {
    return '';
  }

  return normalized
    .split('[]')
    .slice(1)
    .join('[]')
    .replace(/^\./, '')
    .replace(/\./g, '-');
};

const extractArrayReviewTaskRowToken = ({ fieldId = '', wizard = '', normalizedFieldPath = '' }) => {
  if (!fieldId || !wizard) {
    return null;
  }

  const collectionName = getArrayReviewTaskCollectionName(normalizedFieldPath);
  if (!collectionName) {
    return null;
  }

  const prefix = `${wizard}-${collectionName}-`;
  if (!String(fieldId).startsWith(prefix)) {
    return null;
  }

  const suffix = getArrayReviewTaskFieldSuffix(normalizedFieldPath);
  const suffixToken = suffix ? `-${suffix}` : '';
  if (suffixToken && !String(fieldId).endsWith(suffixToken)) {
    return null;
  }

  const endIndex = suffixToken ? fieldId.length - suffixToken.length : fieldId.length;
  if (endIndex <= prefix.length) {
    return null;
  }

  return fieldId.slice(prefix.length, endIndex) || null;
};

const resolveMatchingReviewTasks = (
  tasks = [],
  normalizedFieldPath = '',
  fieldId = '',
  options = {}
) => {
  const { wizard = '' } = options;
  const pathMatchedTasks = (Array.isArray(tasks) ? tasks : [])
    .filter((task) => (
      task?.kind === REVIEW_TASK_KIND.FIELD_CLARIFICATION
      && normalizeReviewTaskFieldPath(task?.fieldPath) === normalizedFieldPath
    ));

  if (!pathMatchedTasks.length) {
    return [];
  }

  const exactFieldIdMatchedTasks = fieldId
    ? pathMatchedTasks.filter((task) => task?.fieldId === fieldId)
    : [];

  if (exactFieldIdMatchedTasks.length) {
    return exactFieldIdMatchedTasks;
  }

  if (!isArrayReviewTaskFieldPath(normalizedFieldPath)) {
    return pathMatchedTasks;
  }

  const currentRowToken = extractArrayReviewTaskRowToken({
    fieldId,
    wizard,
    normalizedFieldPath,
  });

  if (currentRowToken) {
    const rowTokenMatchedTasks = pathMatchedTasks.filter((task) => (
      extractArrayReviewTaskRowToken({
        fieldId: task?.fieldId,
        wizard,
        normalizedFieldPath,
      }) === currentRowToken
    ));

    if (rowTokenMatchedTasks.length) {
      return rowTokenMatchedTasks;
    }

    return [];
  }

  const legacyPathMatchedTasks = pathMatchedTasks.filter((task) => !task?.fieldId);
  return legacyPathMatchedTasks.length === 1 ? legacyPathMatchedTasks : [];
};

const LEGACY_MAX_ADULT_DATE_FIELDS = new Set([
  'individuals[].dob',
]);

const LEGACY_MIN_PERSON_HISTORY_DATE_FIELDS = new Set([
  'individuals[].dob',
]);

const LEGACY_BOOLEAN_DEFAULT_FIELDS = new Map([
  ['Bank_Account_Another_Financial_Institution', false],
  ['inOperation', false],
  ['website', false],
]);

const isUsableSubFieldPath = (path) => {
  if (!path || typeof path !== 'string') {
    return false;
  }

  const normalized = path.trim().toLowerCase();
  return normalized !== 'undefined' && normalized !== 'null' && !normalized.startsWith('undefined.') && !normalized.startsWith('null.');
};

const isAddressParentField = (parentField) => {
  if (typeof parentField !== 'string') {
    return false;
  }

  const normalized = parentField.replace(/\[\]/g, '').trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  const lastSegment = normalized.split('.').pop();
  return lastSegment === 'address' || lastSegment === 'addresslist' || lastSegment.endsWith('address');
};

const moveFieldClassNameToDecorationShell = (fieldNode, uiType) => {
  if (!React.isValidElement(fieldNode) || !FIELD_DECORATION_OUTER_CLASS_UI_TYPES.has(uiType)) {
    return {
      decorationClassName: "",
      decoratedFieldNode: fieldNode,
    };
  }

  const fieldClassName = fieldNode?.props?.className;
  if (!fieldClassName) {
    return {
      decorationClassName: "",
      decoratedFieldNode: fieldNode,
    };
  }

  return {
    decorationClassName: fieldClassName,
    decoratedFieldNode: React.cloneElement(fieldNode, { className: undefined }),
  };
};

const normalizeToMomentDate = (raw) => {
  if (raw === undefined || raw === null) return null;

  if (moment.isMoment(raw)) {
    return raw.clone().startOf('day');
  }

  if (raw instanceof Date && !isNaN(raw)) {
    const parsed = moment(raw);
    return parsed.isValid() ? parsed.startOf('day') : null;
  }

  const asString = String(raw).trim();
  if (!asString) return null;

  const isoParsed = moment(asString, moment.ISO_8601, true);
  if (isoParsed.isValid()) {
    return isoParsed.startOf('day');
  }

  const digitsOnly = asString.replace(/\D/g, '');

  if (/^\d{13}$/.test(digitsOnly)) {
    const parsedMs = moment(Number(digitsOnly));
    return parsedMs.isValid() ? parsedMs.startOf('day') : null;
  }

  if (/^\d{10}$/.test(digitsOnly)) {
    const parsedSec = moment.unix(Number(digitsOnly));
    return parsedSec.isValid() ? parsedSec.startOf('day') : null;
  }

  if (/^\d{8}$/.test(digitsOnly)) {
    const parsed = moment(digitsOnly, 'YYYYMMDD', true);
    return parsed.isValid() ? parsed.startOf('day') : null;
  }

  return null;
};

const normalizeDateRuleFieldPath = (fieldPath) => {
  if (typeof fieldPath !== 'string') {
    return '';
  }
  return fieldPath.replace(/\[\d+\]/g, '[]').trim();
};

export const customValidators = {
  ageValidator: (dataObj, path, value) => {
    if (value < 18 || value > 65) {
      return "Age must be between 18 and 65.";
    }
    return "";
  },
  middleNameValidator: (dataObj, path, value) => {
    if (value?.length > 50) {
      return "Middle name must be less than 50 characters.";
    }
    return "";
  },
  passwordStrengthValidator: (dataObj, path, value) => {
    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[!@#\$%\^\&*\)\(+=._-]/.test(value);
    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      return "Password must contain uppercase, lowercase, number, and special character.";
    }
    return "";
  },

  phoneValidator: (dataObj, path, value) => {
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(value)) {
      return "Phone number must be a 10-digit number.";
    }
    return "";
  },
  emailValidator: (dataObj, path, value) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!re.test(value)) {
      return "Please enter a valid email address.";
    }
    return "";
  },
  dateValidator: (dataObj, path, value) => {
    const inputDate = new Date(value);
    const currentDate = new Date();
    if (inputDate < currentDate) {
      return "Date cannot be in the past.";
    }
    return "";
  },

  annualfinancialStatementValidator: (dataObj, path, value) => {
    return "";
  },

  financialForecastValidator: (dataObj, path, value) => {
    if (dataObj?.hasAnnualStatements == false && value == false) {
      return "We require that you submit a financial forecast in case you do not have financial statements to submit."
    }
    return "";
  },
  licenseHolderValidator: (dataObj, path, value) => {
    if (dataObj?.requiresFundLicense == false && dataObj?.hasFundLicense == false) {
      return "Please provide the licenses you hold and the jurisdiction."
    }
    return "";

  },
  maxSizeValidator: (dataObj, path, value) => {
    const MAX_VALUE = DEFAULT_NUMERIC_MAX_VALUE
    if (value > MAX_VALUE) {
      return `Value cannot exceed ${MAX_VALUE}`
    }
    if (value < 0) {
      return `Value should be greater than 0`
    }
    return ""
  },
  workingStatusIncomeValidator: (dataObj, path, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return "";
    }

    if (isEmploymentStatusRequiringPositiveIncome(dataObj?.employmentStatus) && numericValue <= 0) {
      return "Please enter Annual Salary or Income greater than 0.";
    }

    return "";
  },
  accountActivityCrossField: (dataObj, path) => {
    if (!path) return "";
    return getAccountActivityFieldError({
      field: path,
      validator: "accountActivityCrossField",
    }, dataObj);
  },
  finCenValidator: (dataObj, path, value) => {
    if (dataObj?.hasUsCustomers == true && value == false) {
      return "Are you registered with FinCen? must be Yes."
    }
    return ""
  },
  noneOfTheAboveValidator: (dataObj, path, value) => {
    if ((value ?? [])?.includes("None_of_the_above") && value.length !== 1) {
      return "You can't select 'None of the above' along with other values.";
    }
    return ""
  },
  noneValidator: (dataObj, path, value) => {
    if ((value ?? [])?.includes("None") && value.length !== 1) {
      return "You can't select 'None' along with other values.";
    }
    return ""
  },
  transactionValueValidator: (dataObj, path, value, label) => {
    const MAX_VALUE = DEFAULT_NUMERIC_MAX_VALUE
    if (value > MAX_VALUE) {
      return `${label ?? ""} cannot exceed ${MAX_VALUE}`
    }
    if (value < 0) {
      return `Value should be greater than 0`
    }
    if (!value && value !== 0) {
      return `${label ?? ""} is required.`;
    }
    return ""
  },
  yearValidator: (dataObj, path, value) => {
    if (value && value?.length !== 4) {
      return "Enter a valid year"
    }
    return ""
  },
  dateOfOperationStartedValidator: (dataObj, path, value, label) => {
    if (value === undefined || value === null || value === "") return "";

    const fieldLabel = label || "Date of operations started";
    const operationDate = normalizeToMomentDate(value);

    if (!operationDate) {
      return `Please enter a valid ${fieldLabel} in YYYYMMDD format.`;
    }

    const today = moment().endOf('day');
    if (operationDate.isAfter(today)) {
      return `${fieldLabel} cannot be in the future.`;
    }

    const formationDate = normalizeToMomentDate(dataObj?.Business_Formation_Date);
    if (formationDate && operationDate.isBefore(formationDate)) {
      return `${fieldLabel} cannot be before the Date Company registered.`;
    }

    return "";
  }
};

function getOptionLabel(jsonField, value) {
  const options = jsonField?.options;
  const findFromOptions = () => {
    if (!Array.isArray(options)) return null;
    const match = options.find((opt) => {
      if (!opt) return false;
      return opt.value === value || String(opt.value) === String(value);
    });
    return match?.label ?? null;
  };

  const uiType = jsonField?.uiType;
  const normalizedValue = typeof value === "string" ? value.trim().toUpperCase() : value;

  if (uiType === "countrySelect") {
    const mapMatch = normalizedValue ? CountrySelectService?.cyclosCountryMasterMap?.[normalizedValue] : null;
    if (mapMatch?.label || mapMatch?.Country) {
      return mapMatch?.label ?? mapMatch?.Country ?? null;
    }
    const listMatch = CountrySelectService?.cyclosCountryMaster?.find((opt) => {
      if (!opt) return false;
      const optValue = typeof opt.value === "string" ? opt.value.toUpperCase() : opt.value;
      return optValue === normalizedValue;
    });
    if (listMatch?.label) return listMatch.label;
    if (listMatch?.Country) return listMatch.Country;
  } else if (uiType === "countrySelectLabel") {
    if (typeof value === "string" && value.trim()) {
      return value;
    }
    const labelMatch = CountrySelectService?.cyclosCountryMaster?.find((opt) => opt?.label === value);
    if (labelMatch?.label) return labelMatch.label;
  }

  return findFromOptions();
}

const NOT_AVAILABLE_TEXT = "Not Available";
function formatDisplayValue(jsonField, value, dataObj, arrayIndexing) {
  const uiType = jsonField?.uiType;

  const isEmpty = (val) =>
    val === undefined || val === null ||
    (typeof val === 'string' && val.trim() === '') ||
    (Array.isArray(val) && val.length === 0);

  if (uiType === "phoneWithOTPValidation") {
    const phone = readValue(dataObj, resolveFieldPath(jsonField?.phoneField, arrayIndexing));
    const code = readValue(dataObj, resolveFieldPath(jsonField?.countryCodeField, arrayIndexing));
    if (!phone) return NOT_AVAILABLE_TEXT;
    return `${code ? `+${code} ` : ""}${phone}`.trim();
  }

  if (uiType === "PhoneNumWithValidation") {
    const phone = readValue(dataObj, resolveFieldPath(jsonField?.phoneField, arrayIndexing));
    const code = readValue(dataObj, resolveFieldPath(jsonField?.countryCodeField, arrayIndexing));
    if (!phone) return NOT_AVAILABLE_TEXT;
    return `${code ? `+${code} ` : ""}${phone}`.trim();
  }

  if (uiType === "checkboxMultiple") {
    if (isEmpty(value)) return NOT_AVAILABLE_TEXT;
    const values = (Array.isArray(value) ? value : [value]).map((val) => {
      const label = getOptionLabel(jsonField, val);
      return label ?? String(val);
    }).filter(Boolean);
    return values.length ? values.join(", ") : NOT_AVAILABLE_TEXT;
  }

  if (uiType === "radioMultiple" || uiType === "select" || uiType === "countrySelect" || uiType === "countrySelectLabel") {
    if (isEmpty(value)) return NOT_AVAILABLE_TEXT;
    const label = getOptionLabel(jsonField, value);
    return label ?? String(value);
  }

  if (uiType === "radioBoolean") {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return NOT_AVAILABLE_TEXT;
  }

  if (uiType === "termsAcknowledgement") {
    if (value === true) return "Acknowledged";
    if (value === false) return "Pending acknowledgement";
    return NOT_AVAILABLE_TEXT;
  }

  if (uiType === "signature") {
    return value ? "Signature on file" : NOT_AVAILABLE_TEXT;
  }

  if (uiType === "percentageInput") {
    if (isEmpty(value)) return NOT_AVAILABLE_TEXT;
    const text = String(value).trim();
    return text.endsWith("%") ? text : `${text}%`;
  }

  if (uiType === "amountField" && value && typeof value === 'object') {
    const amount = value?.amount ?? value?.value;
    const currency = value?.currency;
    if (!amount && !currency) return NOT_AVAILABLE_TEXT;
    return [amount, currency].filter(Boolean).join(' ');
  }

  if (typeof value === 'boolean') {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    if (!value.length) return NOT_AVAILABLE_TEXT;
    return value.map((entry) => (entry === null || entry === undefined ? '' : String(entry))).filter(Boolean).join(', ');
  }

  if (isEmpty(value)) {
    return NOT_AVAILABLE_TEXT;
  }

  return String(value);
}

function renderDisplayField({ jsonField, value, className, previousValue, dataObj, arrayIndexing }) {
  const displayValue = formatDisplayValue(jsonField, value, dataObj, arrayIndexing);
  const isChanged = previousValue?.has;
  const containerStyle = {
    background: isChanged ? "rgb(255, 249, 214)" : '#f6f6f6',
    borderRadius: '10px',
    fontSize: 14,
    padding: '12px',
    minHeight: '50px',
    display: 'flex',
    alignItems: 'center',
    color: displayValue === NOT_AVAILABLE_TEXT ? '#999' : '#1f2933',
  };

  return (
    <div className={`inputfield__container ${className || ''}`}>
      {jsonField?.label ? (
        <div
          style={{
            minWidth: '120px',
            fontSize: 16,
            margin: '0px 0px 5px 5px',
            fontWeight: 500,
            textAlign: 'left',
          }}
        >
          {jsonField.label}
          {/* {jsonField?.required && <font color="red">*</font>} */}
        </div>
      ) : null}

      <div
        className={`inputfield__innercontainer inputfield__display ${isChanged ? 'is-changed' : ''}`.trim()}
        style={containerStyle}
      >
        {displayValue}
      </div>

      {jsonField?.description ? (
        <em className="inputfield__desc d-block">{jsonField.description}</em>
      ) : null}

      <PreviousValue value={previousValue?.value} show={previousValue?.has} />
    </div>
  );
}
export const WrapperField = ({
  jsonField,
  dataObj,
  onChange,
  showError,
  onError,
  className: classNameProp,
  disabled,
  customOperator, // Any props related to the operation of field. Can vary for different fields
  customProp, // Any props related to the field. Can vary for different fields
  setLoading,
  loading,
  isForEDD = false, // * to check if the formtype is edd to track diffMap
  haveDependentSubFields = false,
  arrayIndexing = null,
  asDisplay = false,
  customValidation,
  requiredOverride,
}) => {
  const reviewNavigation = useReviewNavigationContext();
  const className = classNameProp ?? jsonField?.uiClassName ?? "";

  const validateField = useMemo(() => {
    return (value, dependentField = null, dependentFieldPath = null) => {
      let error = "";
      const JSONField = dependentField ?? jsonField;
      const validationFieldPath = dependentFieldPath || resolveFieldPath(JSONField?.field, arrayIndexing);

      if (typeof customValidation === "function") {
        const customError = customValidation({
          jsonField: JSONField,
          value,
          dataObj,
          fieldPath: validationFieldPath,
          arrayIndexing,
        });
        if (customError !== undefined) {
          return customError || "";
        }
      }

      const isPrimaryField = !dependentField;
      const resolvedRequiredOverride = isPrimaryField && typeof requiredOverride === "boolean"
        ? requiredOverride
        : null;

      const isConditionallyRequired = resolvedRequiredOverride !== null
        ? resolvedRequiredOverride
        : Boolean(
          JSONField?.required
          || (JSONField?.showIf?.required && shouldShowField(dataObj, JSONField?.showIf, arrayIndexing))
        );
      const isEmptyValue = value === undefined || value === null || value === "" || value?.length == 0;

      if (isConditionallyRequired && isEmptyValue) {
        error = getRequiredErrorMessage(JSONField, "This field is required.");
      } else if (!isEmptyValue && JSONField?.min && value?.length < JSONField.min) {
        error = JSONField?.errorTypes?.length || `Minimum length is ${JSONField?.min}.`;
      } else if (!isEmptyValue && JSONField?.regex && !new RegExp(JSONField?.regex).test(value)) {
        error = JSONField?.errorTypes?.format || JSONField.error || "Invalid format.";
      } else if (!isEmptyValue && JSONField.max && value?.length > JSONField.max) {
        error = JSONField?.errorTypes?.length || `Maximum length is ${JSONField?.max}.`;
      } else if (!isEmptyValue) {
        error = getNumericRangeError(JSONField, value, JSONField?.label);
      }
      // Custom validator logic
      if (JSONField.validator && customValidators?.[JSONField.validator]) {
        const customError = customValidators[JSONField.validator](dataObj, JSONField.field, value, JSONField.label);
        if (customError) {
          error = customError;
        }
      }
      if (!error) {
        const noneOfTheAboveError = getNoneOfTheAboveSelectionError(JSONField, value);
        if (noneOfTheAboveError) {
          error = noneOfTheAboveError;
        }
      }
      if (!error) {
        const accountActivityError = getAccountActivityFieldError(JSONField, dataObj);
        if (accountActivityError) {
          error = accountActivityError;
        }
      }
      return error;
    };
  }, [jsonField, dataObj, customValidation, arrayIndexing, requiredOverride]);

  const parseDate = (dateStr) => {
    return dateStr ? new Date(dateStr) : null;
  };

  const resolveDateBounds = () => {
    const configuredMinDate = parseDate(jsonField?.minDate);
    const configuredMaxDate = parseDate(jsonField?.maxDate);

    const normalizedFieldPath = normalizeDateRuleFieldPath(
      typeof jsonField?.field === 'string' ? jsonField.field : resolvedFieldPath
    );
    const today = moment().startOf('day').toDate();
    const eighteenYearsAgo = moment().startOf('day').subtract(18, 'years').toDate();
    const oneHundredTenYearsAgo = moment().startOf('day').subtract(110, 'years').toDate();

    return {
      minDate: configuredMinDate || (
        LEGACY_MIN_TODAY_DATE_FIELDS.has(normalizedFieldPath)
          ? today
          : LEGACY_MIN_PERSON_HISTORY_DATE_FIELDS.has(normalizedFieldPath)
            ? oneHundredTenYearsAgo
            : null
      ),
      maxDate: configuredMaxDate || (
        LEGACY_MAX_ADULT_DATE_FIELDS.has(normalizedFieldPath)
          ? eighteenYearsAgo
          : (LEGACY_MAX_TODAY_DATE_FIELDS.has(normalizedFieldPath) || normalizedFieldPath === 'dob')
            ? today
            : null
      ),
    };
  };

  const isDisplayMode = asDisplay === true;
  const isValidJsonField = Boolean(jsonField?.field);
  const resolvedFieldPath = isValidJsonField ? resolveFieldPath(jsonField?.field, arrayIndexing) : "";
  const fieldSemanticType = isValidJsonField
    ? getFieldSemanticType(jsonField)
    : null;
  const fieldQuestionText = jsonField?.question || jsonField?.label;
  const normalizedFieldPath = normalizeDateRuleFieldPath(
    typeof jsonField?.field === 'string' ? jsonField.field : resolvedFieldPath
  );
  const configuredDefaultValue = isValidJsonField && Object.prototype.hasOwnProperty.call(jsonField || {}, 'defaultValue')
    ? jsonField?.defaultValue
    : undefined;
  const normalizedConfiguredDefaultValue = fieldSemanticType === 'boolean' && configuredDefaultValue === null
    ? undefined
    : configuredDefaultValue;
  const legacyBooleanDefaultValue = isValidJsonField && fieldSemanticType === 'boolean'
    ? LEGACY_BOOLEAN_DEFAULT_FIELDS.get(normalizedFieldPath)
    : undefined;
  const effectiveBooleanDefaultValue = normalizedConfiguredDefaultValue !== undefined
    ? normalizedConfiguredDefaultValue
    : legacyBooleanDefaultValue;
  const rawFieldValue = isValidJsonField ? readValue(dataObj, resolvedFieldPath, arrayIndexing) : undefined;
  const currentFieldValue = fieldSemanticType === 'boolean'
    ? normalizeBooleanLikeValue(rawFieldValue)
    : rawFieldValue;
  const fieldDefaultValue = isValidJsonField
    ? (
      effectiveBooleanDefaultValue !== undefined
        ? effectiveBooleanDefaultValue
        : getFieldResetValue(jsonField)
    )
    : null;
  const isFieldVisible = isValidJsonField ? shouldShowField(dataObj, jsonField?.showIf, arrayIndexing) : false;
  const effectiveRequired = isValidJsonField
    ? (typeof requiredOverride === "boolean"
      ? requiredOverride
      : Boolean(jsonField?.required || (jsonField?.showIf?.required && isFieldVisible)))
    : false;
  const fieldPreviousValue = isValidJsonField && isForEDD
    ? getPrevValueFromDiffMap(dataObj, jsonField?.field, arrayIndexing)
    : { value: undefined, has: false };
  const shouldShowErr = showError && !isDisplayMode;
  const fieldErrorMessage = isDisplayMode || !isValidJsonField ? "" : validateField(currentFieldValue);

  const resolveDependentFieldConfig = (fieldPath) => {
    if (!haveDependentSubFields) {
      return jsonField;
    }

    const baseFieldLabel = fieldPath?.replace(/\[\d+\]/g, '[]');
    return customProp?.find((entry) => (
      entry?.field === baseFieldLabel || entry?.field === fieldPath
    )) || jsonField;
  };

  const buildPrimaryFieldUpdate = (value) => (
    isValidJsonField
      ? {
        fieldPath: resolvedFieldPath,
        value,
        fieldConfig: jsonField,
      }
      : null
  );

  const buildSubFieldUpdate = (fieldPath, value) => {
    const resolvedSubFieldPath = resolveFieldPath(fieldPath, arrayIndexing);

    if (!isUsableSubFieldPath(resolvedSubFieldPath)) {
      return null;
    }

    return {
      fieldPath: resolvedSubFieldPath,
      value,
      fieldConfig: resolveDependentFieldConfig(fieldPath),
    };
  };

  const applyValidatedUpdates = (updates = []) => {
    if (isDisplayMode) {
      return null;
    }

    const normalizedUpdates = updates.filter(Boolean);
    if (!normalizedUpdates.length) {
      return null;
    }

    normalizedUpdates.forEach(({ value, fieldConfig, fieldPath }) => {
      const error = validateField(value, fieldConfig, fieldPath);
      if (fieldPath === resolvedFieldPath) {
        onError(error || null);
      } else if (error) {
        onError(error, fieldPath);
      } else {
        onError(null, fieldPath);
      }
    });

    let nextData = _.cloneDeep(dataObj || {});
    normalizedUpdates.forEach(({ fieldPath, value, fieldConfig }) => {
      const baselineValue = getFieldBaselineValue(fieldConfig);
      nextData = setValue(nextData, fieldPath, value, isForEDD, baselineValue, arrayIndexing);
    });

    if (typeof onChange === 'function') {
      if (onChange.length >= 2 && normalizedUpdates.length === 1) {
        const [{ fieldPath, value }] = normalizedUpdates;
        onChange(fieldPath, value, nextData);
      } else {
        onChange(nextData);
      }
    } else {
      console.warn('WrapperField: onChange is not a function', {
        field: jsonField?.field,
        resolvedFieldPath,
      });
    }

    return nextData;
  };

  const handleChange = (value) => {
    applyValidatedUpdates([buildPrimaryFieldUpdate(value)]);
  };
  const previousVisibilityRef = useRef(null);
  const trackedFieldPathRef = useRef("");
  const lastVisibleValueRef = useRef({ has: false, value: undefined });

  useEffect(() => {
    if (trackedFieldPathRef.current !== resolvedFieldPath) {
      trackedFieldPathRef.current = resolvedFieldPath;
      previousVisibilityRef.current = null;
      lastVisibleValueRef.current = { has: false, value: undefined };
    }

    if (!isValidJsonField || isDisplayMode) {
      previousVisibilityRef.current = isFieldVisible;
      if (isFieldVisible) {
        lastVisibleValueRef.current = { has: true, value: currentFieldValue };
      }
      return;
    }

    const wasVisible = previousVisibilityRef.current;
    if (wasVisible === null) {
      previousVisibilityRef.current = isFieldVisible;
      if (isFieldVisible) {
        lastVisibleValueRef.current = { has: true, value: currentFieldValue };
      }
      return;
    }

    const becameHidden = wasVisible === true && !isFieldVisible;
    const becameVisible = wasVisible === false && isFieldVisible;

    if (becameHidden) {
      lastVisibleValueRef.current = { has: true, value: currentFieldValue };
      previousVisibilityRef.current = false;
      // Fields hidden via a `hidePinCode` flag (country with a fixed postal code, e.g. HK -> 999077)
      // were just auto-filled with an authoritative value that must persist and be saved; resetting
      // them to the default here is exactly what broke the postal-code auto-populate, so skip it.
      if (!isHidePinCodeGatedShowIf(jsonField?.showIf)
        && !areFieldValuesEqual(resolvedFieldPath, currentFieldValue, fieldDefaultValue)) {
        applyValidatedUpdates([buildPrimaryFieldUpdate(fieldDefaultValue)]);
      }
      return;
    }

    if (becameVisible) {
      previousVisibilityRef.current = true;
      const diffMapRestoreCandidate = (
        fieldPreviousValue?.has
        && !(fieldSemanticType === 'boolean' && typeof fieldPreviousValue?.value !== 'boolean')
      )
        ? fieldPreviousValue.value
        : undefined;
      const restoreCandidate = lastVisibleValueRef.current.has
        ? lastVisibleValueRef.current.value
        : diffMapRestoreCandidate;

      // hidePinCode-gated postal fields are owned by the country handlers (which set/clear the
      // pincode pair atomically); restoring a previous country's zip here would resurrect stale
      // data and, worse, ping-pong against a concurrent country write until React aborts with
      // "Maximum update depth exceeded" (country visibly toggling). Mirror of the reset skip above.
      if (
        !isHidePinCodeGatedShowIf(jsonField?.showIf) &&
        restoreCandidate !== undefined &&
        areFieldValuesEqual(resolvedFieldPath, currentFieldValue, fieldDefaultValue) &&
        !areFieldValuesEqual(resolvedFieldPath, currentFieldValue, restoreCandidate)
      ) {
        applyValidatedUpdates([buildPrimaryFieldUpdate(restoreCandidate)]);
        return;
      }
    }

    previousVisibilityRef.current = isFieldVisible;
    if (isFieldVisible) {
      lastVisibleValueRef.current = { has: true, value: currentFieldValue };
    }
  }, [
    arrayIndexing,
    currentFieldValue,
    fieldDefaultValue,
    fieldPreviousValue?.has,
    fieldPreviousValue?.value,
    fieldSemanticType,
    isDisplayMode,
    isFieldVisible,
    isForEDD,
    isValidJsonField,
    resolvedFieldPath,
  ]);

  // Generate stable ID for QA automation: ${wizard}-${fieldName}
  const fieldId = isValidJsonField
    ? generateFieldId(jsonField, arrayIndexing, dataObj)
    : "";
  const normalizedReviewFieldPath = isValidJsonField
    ? normalizeReviewTaskFieldPath(jsonField?.field)
    : "";
  const matchingReviewTasks = useMemo(() => {
    if (!isValidJsonField || !jsonField?.field) {
      return [];
    }

    const resolvedMatchingTasks = resolveMatchingReviewTasks(
      reviewNavigation?.visibleTasks,
      normalizedReviewFieldPath,
      fieldId,
      {
        arrayIndexing,
        wizard: jsonField?.wizard,
      }
    );

    return resolvedMatchingTasks.sort((left, right) => (right?.createdAt || 0) - (left?.createdAt || 0));
  }, [arrayIndexing, fieldId, isValidJsonField, jsonField?.field, jsonField?.wizard, normalizedReviewFieldPath, reviewNavigation?.visibleTasks]);
  const activeReviewTask = matchingReviewTasks[0] || null;
  const isSelectedReviewTask = activeReviewTask?.taskKey
    && reviewNavigation?.selectedTask?.taskKey === activeReviewTask.taskKey;

  const commitReviewTaskValue = useCallback((nextValue) => {
    if (!activeReviewTask || isDisplayMode) {
      return;
    }

    reviewNavigation?.commitFieldTask?.({
      fieldPath: jsonField?.field,
      fieldId,
      arrayIndexing,
      wizard: jsonField?.wizard,
      nextValue,
    });
  }, [activeReviewTask, arrayIndexing, fieldId, isDisplayMode, jsonField?.field, jsonField?.wizard, reviewNavigation]);

  const renderWithReviewDecoration = (fieldNode) => {
    if (!activeReviewTask) {
      return fieldNode;
    }

    const {
      decorationClassName,
      decoratedFieldNode,
    } = moveFieldClassNameToDecorationShell(fieldNode, jsonField?.uiType);

    return (
      <FieldClarificationDecorator
        task={activeReviewTask}
        isSelected={isSelectedReviewTask}
        className={decorationClassName}
      >
        {decoratedFieldNode}
      </FieldClarificationDecorator>
    );
  };

  // Early validation - ensure jsonField is properly defined
  if (!isValidJsonField) {
    const warningKey = String(jsonField?.field || jsonField?.wizard || 'undefined');
    if (!loggedInvalidJsonFieldWarnings.has(warningKey)) {
      loggedInvalidJsonFieldWarnings.add(warningKey);
      console.warn('WrapperField: Invalid jsonField provided', jsonField);
    }
    return null;
  }

  if (!isFieldVisible) {
    return null;
  }

  if (isDisplayMode) {
    return renderWithReviewDecoration(renderDisplayField({
      jsonField,
      value: currentFieldValue,
      className,
      previousValue: fieldPreviousValue,
      dataObj,
      arrayIndexing,
    }));
  }

  const isPlainVariant = customProp === "plain";


  switch (jsonField?.uiType) {
    case "amountField":
      return renderWithReviewDecoration(
        <AmountField
          id={fieldId}
          data-testid={fieldId}
          label={jsonField?.label}
          noLabel={isPlainVariant || Boolean(jsonField?.noLabel) || !jsonField?.label}
          plain={isPlainVariant}
          isDisabled={disabled}
          placeholder={jsonField?.placeholder}
          onChange={(v) => {
            handleChange(v);
            commitReviewTaskValue(v);
          }}
          required={effectiveRequired}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          className={className}
          prevValue={fieldPreviousValue}
        />
      )

    case "input":
      return renderWithReviewDecoration(
        <InputField
          id={fieldId}
          data-testid={fieldId}
          label={jsonField?.label}
          noLabel={isPlainVariant || Boolean(jsonField?.noLabel) || !jsonField?.label}
          isDisabled={disabled}
          placeholder={jsonField?.placeholder}
          onChange={(v) => handleChange(v)}
          required={effectiveRequired}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          className={className}
          plain={isPlainVariant}
          maxLength={jsonField?.max}
          desc={jsonField?.description}
          regex={jsonField?.uiRegex ? new RegExp(jsonField?.uiRegex) : undefined}
          prevValue={fieldPreviousValue}
          onblur={(v) => commitReviewTaskValue(typeof v === "string" ? v.trim() : v)}
        />
      );

    case "textarea":
      return renderWithReviewDecoration(
        <CustomTextArea
          id={fieldId}
          data-testid={fieldId}
          label={jsonField?.label}
          noLabel={Boolean(jsonField?.noLabel) || !jsonField?.label}
          placeholder={jsonField?.placeholder}
          required={effectiveRequired}
          onChange={(v) => handleChange(v)}
          onCommit={(v) => commitReviewTaskValue(v)}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          className={className}
          isDisabled={disabled}
          prevValue={fieldPreviousValue}
        />
      );

    case "select":
      return renderWithReviewDecoration(
        <SelectField
          id={fieldId}
          data-testid={fieldId}
          label={jsonField?.label}
          noLabel={isPlainVariant || Boolean(jsonField?.noLabel) || !jsonField?.label}
          plain={isPlainVariant}
          placeholder={jsonField?.placeholder || "Select an option"}
          data={jsonField?.options || []}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          required={effectiveRequired}
          onChange={(v) => {
            const nextValue = v?.value;
            handleChange(nextValue);
            commitReviewTaskValue(nextValue);
          }}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          isDisabled={disabled || jsonField?.isDisabled || false}
          className={className}
          prevValue={fieldPreviousValue}
        />
      );

    case "selectMultiple": {
      const rawValue = readValue(dataObj, resolvedFieldPath, arrayIndexing);
      const selectedValues = Array.isArray(rawValue) ? rawValue : [];
      const selectedOptions = (jsonField?.options || []).filter((option) => (
        selectedValues.includes(option?.value)
      ));

      return renderWithReviewDecoration(
        <SelectFieldMultiple
          id={fieldId}
          data-testid={fieldId}
          className={className}
          labelStyle={jsonField?.labelStyle}
          data={jsonField?.options || []}
          label={jsonField?.label}
          plain={isPlainVariant}
          placeholder={jsonField?.placeholder}
          value={selectedOptions}
          onChange={(options) => {
            const nextValue = Array.isArray(options) ? options.map((option) => option?.value) : [];
            handleChange(nextValue);
            commitReviewTaskValue(nextValue);
          }}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          isDisabled={disabled || jsonField?.isDisabled || false}
          required={effectiveRequired}
        />
      );
    }

    case "checkboxMultiple":
      return renderWithReviewDecoration(
        <CheckBoxFieldMutiple
          id={fieldId}
          data-testid={fieldId}
          className={className}
          label={jsonField?.label}
          labelStyle={jsonField?.labelStyle || { fontSize: 16 }}
          data={jsonField?.options || []}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing) || []}
          onChange={(v) => {
            handleChange(v);
            commitReviewTaskValue(v);
          }}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          isDisabled={disabled || jsonField?.isDisabled || false}
          optionClassName={jsonField?.optionClassName}
          required={effectiveRequired}
          desc={jsonField?.description}
          prevValue={fieldPreviousValue}
          showChanges={isForEDD}
        />
      );

    case "radioBoolean":
      return renderWithReviewDecoration(
        <QuestionRadioGroup
          id={fieldId}
          testId={fieldId}
          question={fieldQuestionText}
          field={resolvedFieldPath}
          data={dataObj}
          handleChange={(field, v) => {
            const nextData = applyValidatedUpdates([buildPrimaryFieldUpdate(v)]);
            commitReviewTaskValue(v);
            if (typeof customOperator === "function") {
              customOperator(v, nextData);
            }
          }}
          style={jsonField?.style || { fontSize: 16 }}
          error={fieldErrorMessage}
          hasError={showError}
          isDisabled={disabled || jsonField?.isDisabled || false}
          defaultValue={effectiveBooleanDefaultValue ?? null}
          required={effectiveRequired}
          prevValue={fieldPreviousValue}
          preventDefault={customProp}
          desc={jsonField?.description}
          tooltip={jsonField?.tooltip}
        />
      );

    case "radioMultiple":
      return renderWithReviewDecoration(
        <QuestionRadioGroupMultiple
          id={fieldId}
          testId={fieldId}
          question={fieldQuestionText}
          field={resolvedFieldPath}
          data={dataObj}
          returnsArray={getFieldSemanticType(jsonField) === 'array'}
          handleChange={(field, v) => {
            applyValidatedUpdates([buildPrimaryFieldUpdate(v)]);
            commitReviewTaskValue(v);
          }}
          style={jsonField?.style || { fontSize: 15 }}
          options={jsonField?.options || []}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          isDisabled={disabled || jsonField?.isDisabled || false}
          required={effectiveRequired}
          prevValue={fieldPreviousValue}
          optionsRowClassName={jsonField?.optionsRowClassName}
          optionClassName={jsonField?.optionClassName}
        />
      );

    case "date":
      const { minDate, maxDate } = resolveDateBounds();
      return renderWithReviewDecoration(
        <AntDateSelect
          id={fieldId}
          data-testid={fieldId}
          placeholder={jsonField?.placeholder || "dd mmm yyyy"}
          className={className || "col-sm-6 mb-0"}
          displayFormat="dd MMM yyyy"
          format="dd-mmm-yyyy"
          label={jsonField?.label}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          onChange={(v) => {
            handleChange(v);
            commitReviewTaskValue(v);
          }}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          isDisabled={disabled || jsonField?.isDisabled || false}
          plain={isPlainVariant}
          minDate={minDate}
          maxDate={maxDate}
          required={effectiveRequired}
          prevValue={fieldPreviousValue}
        />
      );

    case "addressAutocomplete":
      return renderWithReviewDecoration(
        <AddressAutocompleter
          id={fieldId}
          data-testid={fieldId}
          isDisabled={disabled}
          placeholder={jsonField?.placeholder}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          onChange={(v) => handleChange(v)}
          onCommit={(v) => commitReviewTaskValue(v)}
          onSelect={async (components) => {
            const parentFieldResolved = resolveFieldPath(jsonField?.parentField, arrayIndexing);
            if (!isUsableSubFieldPath(parentFieldResolved) || !isAddressParentField(jsonField?.parentField)) {
              return;
            }
            // Ensure postal-code config is loaded before reading the country's default pincode
            // (cached fetch = no-op once loaded). See the country onChange above for the rationale.
            try {
              await pincodeService.fetch();
            } catch (e) {
              // Non-blocking: fall through with whatever config is cached.
            }
            const nextUpdates = [
              buildSubFieldUpdate(`${parentFieldResolved}.buildingNumber`, components.buildingNumber),
              buildSubFieldUpdate(`${parentFieldResolved}.street`, components.street),
              buildSubFieldUpdate(`${parentFieldResolved}.city`, components.city),
              buildSubFieldUpdate(`${parentFieldResolved}.state`, components.state),
            ];

            // Only touch country/hidePinCode/pinCode when the resolved place actually carries a
            // country. Some geocode results have none, and blanking a country the user already
            // selected (plus resetting the pincode pair) made the country field flicker/toggle.
            if (components.country) {
              nextUpdates.push(buildSubFieldUpdate(`${parentFieldResolved}.country`, components.country));
              const defaultPincode = getDefaultValue(components.country);
              if (defaultPincode) {
                nextUpdates.push(buildSubFieldUpdate(`${parentFieldResolved}.hidePinCode`, true));
                nextUpdates.push(buildSubFieldUpdate(`${parentFieldResolved}.pinCode`, defaultPincode));
              } else {
                nextUpdates.push(buildSubFieldUpdate(`${parentFieldResolved}.hidePinCode`, false));
                nextUpdates.push(buildSubFieldUpdate(`${parentFieldResolved}.pinCode`, components.pinCode));
              }
            }

            applyValidatedUpdates(nextUpdates);
            commitReviewTaskValue(components.street);
          }}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          desc={jsonField?.description}
          prevValue={fieldPreviousValue}
        />
      );

    case "pinCode":
      const parentFieldResolved = resolveFieldPath(jsonField?.parentField, arrayIndexing);
      const countryValue = readValue(dataObj, `${parentFieldResolved}.country`, null);

      return renderWithReviewDecoration(
        <PincodeField
          key={"pinCode"}
          id={fieldId}
          data-testid={fieldId}
          countryCode={countryValue}
          placeholder={jsonField?.placeholder}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          onChange={(v) => handleChange(v)}
          onCommit={(v) => commitReviewTaskValue(v)}
          showErr={shouldShowErr}
          maxLength={40}
          isDisabled={disabled}
          className={className}
          prevValue={fieldPreviousValue}
          onError={(v) => {
            if (v)
              onError(v)
            else
              onError("")
          }}
        />
      );

    case "countrySelect":
      return renderWithReviewDecoration(
        <SelectField
          id={fieldId}
          data-testid={fieldId}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          data={CountrySelectService?.cyclosCountryMaster}
          isDisabled={disabled}
          required={effectiveRequired}
          noLabel={isPlainVariant || Boolean(jsonField?.noLabel) || !jsonField?.label}
          plain={isPlainVariant}
          placeholder={jsonField?.placeholder}
          label={jsonField?.label}
          desc={jsonField?.description}
          className={className}
          prevValue={fieldPreviousValue}
          onChange={async (v) => {
            // No-op echo guard: selects can sync a value we just wrote straight back (e.g. the
            // SelectField props/value effects). Re-running the async write for an identical
            // country would re-clone stale data and can sustain an update loop.
            if ((v?.value ?? '') === (readValue(dataObj, resolvedFieldPath, arrayIndexing) ?? '')) {
              return;
            }
            const parentFieldResolved = resolveFieldPath(jsonField?.parentField, arrayIndexing);
            if (!isUsableSubFieldPath(parentFieldResolved) || !isAddressParentField(jsonField?.parentField)) {
              applyValidatedUpdates([buildPrimaryFieldUpdate(v?.value)]);
              commitReviewTaskValue(v?.value);
              return;
            }
            // Ensure the postal-code config is loaded BEFORE reading the country's default pincode.
            // The forms prefetch it fire-and-forget (not awaited), so on a fast country change the
            // records could still be empty and getDefaultValue would wrongly return null - which is
            // why e.g. Hong Kong (default 999077) was not auto-populating. fetch() is cached, so this
            // is a no-op once records are loaded. A single applyValidatedUpdates below keeps the
            // country + pincode writes atomic (avoids a stale-dataObj clobber across the await).
            try {
              await pincodeService.fetch();
            } catch (e) {
              // Non-blocking: fall through with whatever config is cached.
            }
            const defaultPincode = getDefaultValue(v?.value);
            const nextUpdates = [buildPrimaryFieldUpdate(v?.value)];
            if (defaultPincode) {
              nextUpdates.push(buildSubFieldUpdate(`${parentFieldResolved}.hidePinCode`, true));
              nextUpdates.push(buildSubFieldUpdate(`${parentFieldResolved}.pinCode`, defaultPincode));
            } else {
              nextUpdates.push(buildSubFieldUpdate(`${parentFieldResolved}.hidePinCode`, false));
            }
            applyValidatedUpdates(nextUpdates);
            commitReviewTaskValue(v?.value);
          }}
        />
      );

    case "countrySelectLabel":
      return renderWithReviewDecoration(
        <SelectField
          id={fieldId}
          data-testid={fieldId}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          data={CountrySelectService?.cyclosCountryMaster?.map(v => ({ value: v.label, label: v.label }))}
          required={effectiveRequired}
          isDisabled={disabled}
          noLabel={isPlainVariant || Boolean(jsonField?.noLabel) || !jsonField?.label}
          plain={isPlainVariant}
          placeholder={jsonField?.placeholder}
          label={jsonField?.label}
          desc={jsonField?.description}
          className={className}
          prevValue={fieldPreviousValue}
          onChange={(v) => {
            handleChange(v?.value)
            commitReviewTaskValue(v?.value);
          }}
        />
      );



    case "phoneWithOTPValidation":
      const phoneOtpWrapperProps = customProp && typeof customProp === "object" ? customProp : {};
      const otpVerifyHandler = typeof customProp === "function"
        ? customProp
        : (phoneOtpWrapperProps?.onOtpVerify || (() => { }));
      return renderWithReviewDecoration(
        <PhoneWithOtpValidation
          id={fieldId}
          data-testid={fieldId}
          group={jsonField?.group}
          required={effectiveRequired}
          label={jsonField?.label}
          placeholder={jsonField?.placeholder}
          form_id={dataObj?._id}
          formData={dataObj}
          value={{
            phone: readValue(dataObj, resolveFieldPath(jsonField?.phoneField, arrayIndexing)),
            countryCode: readValue(dataObj, resolveFieldPath(jsonField?.countryCodeField, arrayIndexing)),
            phoneVerifiedViaOTP: readValue(dataObj, resolveFieldPath(jsonField?.phoneVerifiedViaOTPField, arrayIndexing))
          }}
          onChange={(v) => {
            applyValidatedUpdates([
              buildSubFieldUpdate(jsonField?.phoneField, v?.phone),
              buildSubFieldUpdate(jsonField?.countryCodeField, v?.countryCode),
              buildSubFieldUpdate(jsonField?.phoneVerifiedViaOTPField, v?.phoneVerifiedViaOTP),
            ]);
          }}
          onCommit={(v) => commitReviewTaskValue(v?.phone)}
          onShouldPerformOtpVerificationChange={(v) => {
            if (!disabled && typeof customOperator === "function")
              customOperator(v);
          }}
          onError={(v) => {
            if (disabled) return;
            if (v?.hasError)
              onError(v?.errorMsg)
            else
              onError("")
          }}
          onOtpVerify={otpVerifyHandler}
          err={validateField(readValue(dataObj, resolveFieldPath(jsonField?.phoneField, arrayIndexing)))}
          showErr={shouldShowErr}
          isDisabled={disabled}
          className={className}
          prevValue={fieldPreviousValue}
          notifyParent={phoneOtpWrapperProps?.notifyParent}
          forAgent={phoneOtpWrapperProps?.forAgent}
          reloadApplicationInConsole={phoneOtpWrapperProps?.reloadApplicationInConsole}
          individual_id={phoneOtpWrapperProps?.individual_id}
          validateOnly={phoneOtpWrapperProps?.validateOnly ?? jsonField?.validateOnly}
          type={phoneOtpWrapperProps?.type ?? jsonField?.phoneType}
        />
      );


    case "PhoneNumWithValidation":
      // Phone fields are represented as 2 sub-fields (phone + countryCode). For change-highlighting
      // we treat the field as "changed" if either sub-field changed, and we show the previous
      // pair for clarity.
      const phoneWrapperProps = customProp && typeof customProp === "object" ? customProp : {};
      const phonePrev = getPrevValueFromDiffMap(
        dataObj,
        isForEDD ? jsonField?.phoneField : "",
        arrayIndexing
      );
      const codePrev = getPrevValueFromDiffMap(
        dataObj,
        isForEDD ? jsonField?.countryCodeField : "",
        arrayIndexing
      );
      const currentPhone = readValue(dataObj, resolveFieldPath(jsonField?.phoneField, arrayIndexing));
      const currentCode = readValue(dataObj, resolveFieldPath(jsonField?.countryCodeField, arrayIndexing));
      const combinedPrevValue = {
        has: Boolean(phonePrev?.has || codePrev?.has),
        value: {
          phone: phonePrev?.value ?? currentPhone ?? "",
          countryCode: codePrev?.value ?? currentCode ?? "",
        },
      };
      return renderWithReviewDecoration(
        <PhoneNumWithValidation
          id={fieldId}
          data-testid={fieldId}
          group={jsonField?.group}
          className={className}
          label={jsonField?.label}
          form_id={dataObj?._id}
          placeholder={jsonField?.placeholder}
          value={{
            phone: readValue(dataObj, resolveFieldPath(jsonField?.phoneField, arrayIndexing)),
            countryCode: readValue(dataObj, resolveFieldPath(jsonField?.countryCodeField, arrayIndexing)),
          }}
          onChange={(v) => {
            applyValidatedUpdates([
              buildSubFieldUpdate(jsonField?.phoneField, v?.phone),
              buildSubFieldUpdate(jsonField?.countryCodeField, v?.countryCode),
            ]);
          }}
          onCommit={(v) => commitReviewTaskValue(v?.phone)}
          isDisabled={disabled}
          notifyParent={phoneWrapperProps?.notifyParent || (() => { })}
          onError={(v) => {
            if (disabled) return;
            if (v?.hasError)
              onError(v?.errorMsg)
            else
              onError("")
          }}
          required={effectiveRequired}
          err={validateField(readValue(dataObj, resolveFieldPath(jsonField?.phoneField, arrayIndexing)))}
          showErr={shouldShowErr}
          formData={dataObj}
          validateOnly={phoneWrapperProps?.validateOnly ?? jsonField?.validateOnly ?? true}
          type={phoneWrapperProps?.type ?? jsonField?.phoneType}
          individual_id={phoneWrapperProps?.individual_id}
          forAgent={phoneWrapperProps?.forAgent}
          reloadApplicationInConsole={phoneWrapperProps?.reloadApplicationInConsole}
          prevValue={combinedPrevValue}
          skipRemoteValidation={isForEDD}
        />
      );


    case "username":
      return renderWithReviewDecoration(
        <UserNameField
          id={fieldId}
          data-testid={fieldId}
          setIsValidUsername={(v) => {
            if (disabled) return;
            if (v === false) onError("Username not available");
            else onError("");
            customOperator?.(v);
          }}
          isValidUsername={customProp}
          loading={loading}
          handleLoading={setLoading}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          applicationId={dataObj?._id}
          onChange={(v) => {
            handleChange(v);
            commitReviewTaskValue(v);
          }}
          required={effectiveRequired}
          showErr={shouldShowErr}
          isDisabled={disabled}
          err={fieldErrorMessage}
          prevValue={fieldPreviousValue}
        />
      );

    case "ssn":
      return renderWithReviewDecoration(
        <SSNField
          id={fieldId}
          data-testid={fieldId}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          onChange={(v) => {
            handleChange(v);
            commitReviewTaskValue(v);
          }}
          className={className}
          showErr={shouldShowErr}
          required={effectiveRequired}
          notifyParent={(v) => {
            if (!v) onError("Please enter a valid SSN.");
            else onError("");
          }}
          group={jsonField?.group}
          applicationId={dataObj?._id}
          err={fieldErrorMessage}
          isDisabled={disabled}
          prevValue={fieldPreviousValue}
        />
      );

    case "ssnMasked":
      return renderWithReviewDecoration(
        <SSNFieldMasked
          id={fieldId}
          data-testid={fieldId}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          onChange={(v) => {
            handleChange(v);
            commitReviewTaskValue(v);
          }}
          className={className}
          showErr={shouldShowErr}
          required={effectiveRequired}
          notifyParent={(v) => {
            if (!v) onError("Please enter a valid SSN.");
            else onError("");
          }}
          group={jsonField?.group}
          applicationId={dataObj?._id}
          err={fieldErrorMessage}
          isDisabled={disabled}
          prevValue={fieldPreviousValue}
          masking={true}
        />
      );

    case "currencyField":
      return renderWithReviewDecoration(
        <CurrencyField
          id={fieldId}
          data-testid={fieldId}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          label={jsonField?.label}
          noLabel={isPlainVariant || Boolean(jsonField?.noLabel) || !jsonField?.label}
          placeholder={jsonField?.placeholder}
          className={className}
          onChange={(v) => handleChange(v)}
          onCommit={(v) => commitReviewTaskValue(v)}
          isDisabled={disabled}
          plain={isPlainVariant}
          showErr={shouldShowErr}
          err={fieldErrorMessage}
          required={effectiveRequired}
          prevValue={fieldPreviousValue}
          maxValue={jsonField?.maxValue}
          minValue={jsonField?.minValue}
        />
      );

    case "numberField":
      return renderWithReviewDecoration(
        <NumberField
          id={fieldId}
          data-testid={fieldId}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          required={effectiveRequired}
          label={jsonField?.label}
          onChange={(v) => handleChange(v)}
          onCommit={(v) => commitReviewTaskValue(v)}
          variant={customProp}
          showInputGroupText={Boolean(jsonField?.showInputGroupText) || customOperator}
          inputGroupText={jsonField?.inputGroupText}
          placeholder={jsonField?.placeholder}
          showErr={shouldShowErr}
          isRequired={effectiveRequired}
          error={fieldErrorMessage}
          isDisabled={disabled}
          prevValue={fieldPreviousValue}
          description={jsonField?.description}
          inputRegex={jsonField?.uiRegex}
          maxValue={jsonField?.maxValue}
          minValue={jsonField?.minValue}
          textAlign={jsonField?.textAlign}
        />
      );

    case "emailWithValidation":
      return renderWithReviewDecoration(
        <EmailWithValidation
          id={fieldId}
          data-testid={fieldId}
          group={jsonField?.group || "Businesses_Lite"}
          label={jsonField?.label}
          placeholder={jsonField?.placeholder}
          className={className}
          form_id={dataObj?._id}
          notifyParent={(v) => {
            // Optional: Handle email validation status
          }}
          onError={(v) => {
            if (v?.hasError) {
              onError(v?.errorMsg);
            } else {
              onError("");
            }
          }}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          onChange={(v) => handleChange(v)}
          onCommit={(v) => commitReviewTaskValue(v)}
          err={fieldErrorMessage}
          showErr={shouldShowErr}
          isDisabled={disabled}
          required={effectiveRequired}
          prevValue={fieldPreviousValue}
        />
      );

    case "yearPicker":
      return renderWithReviewDecoration(
        <YearPicker
          id={fieldId}
          data-testid={fieldId}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          onChange={(v) => {
            handleChange(v);
            commitReviewTaskValue(v);
          }}
          label={jsonField?.label}
          placeholder={jsonField?.placeholder}
          required={effectiveRequired}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          maxYear={moment().year()}
          format="yyyy"
          isDisabled={disabled || jsonField?.isDisabled || false}
          className={className}
          prevValue={fieldPreviousValue}
          description={jsonField?.description}
          desc={jsonField?.description}
          displayFormat="yyyy"
        />
      );

    case "signature":
      return renderWithReviewDecoration(
        <SignPad
          id={fieldId}
          data-testid={fieldId}
          asLabel={disabled}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          onChange={(sign) => {
            handleChange(sign);
            commitReviewTaskValue(sign);
          }}
        />
      );

    case "termsAcknowledgement":
      return renderWithReviewDecoration(
        <TermsAcknowledgement
          id={fieldId}
          data-testid={fieldId}
          height={300}
          contentType={jsonField?.contentType}
          fullContent={Boolean(jsonField?.fullContent)}
          isDisabled={disabled}
          isChecked={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          onAccept={(v) => {
            if (!disabled) {
              handleChange(v);
              commitReviewTaskValue(v);
            }
          }}
        />
      );

    case "percentageInput":
      return renderWithReviewDecoration(
        <PercentageInput
          id={fieldId}
          data-testid={fieldId}
          className={className}
          label={jsonField?.label}
          placeholder={jsonField?.placeholder}
          required={effectiveRequired}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          onChange={(v) => handleChange(v)}
          onCommit={(v) => commitReviewTaskValue(v)}
          isDisabled={disabled}
          error={fieldErrorMessage}
          showErr={shouldShowErr}
          prevValue={fieldPreviousValue}
        />
      );

    case "yearField":
      return renderWithReviewDecoration(
        <YearPicker
          id={fieldId}
          data-testid={fieldId}
          value={readValue(dataObj, resolvedFieldPath, arrayIndexing)}
          onChange={(v) => {
            handleChange(v);
            commitReviewTaskValue(v);
          }}
          required={effectiveRequired}
          label={jsonField?.label}
          placeholder={jsonField?.placeholder}
          showErr={shouldShowErr}
          error={fieldErrorMessage}
          maxYear={moment().year()}
          format="yyyy"
          prevValue={fieldPreviousValue}
          description={jsonField?.description}
          desc={jsonField?.description}
          className={className}
          displayFormat="yyyy"
          isDisabled={disabled || jsonField?.isDisabled || false}
        />
      );
    default:
      return null;
  }
};
